import React, { useEffect, useState, useCallback, useRef } from 'react';
import axios from 'axios';
import { FileText, Upload, Trash2, Pencil, Check, X, ChevronUp, ChevronDown, Eye, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAX_BYTES = 15 * 1024 * 1024;
const fmtSize = (b) => (b >= 1024 * 1024 ? `${(b / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);

const PdfRow = ({ pdf, index, total, onRename, onDelete, onMove, onPreview }) => {
  const [editing, setEditing] = useState(false);
  const [title, setTitle] = useState(pdf.title);
  const commit = async () => {
    const next = title.trim();
    if (!next) {
      toast.error('Title is required');
      setTitle(pdf.title);
      setEditing(false);
      return;
    }
    if (next !== pdf.title) await onRename(pdf, next);
    setEditing(false);
  };
  return (
    <div className="flex items-center gap-3 p-3 rounded-2xl bg-[#0f0f0f] border border-white/10" data-testid={`menu-pdf-row-${pdf.id}`}>
      <div className="w-10 h-10 rounded-xl bg-[#FF007F]/10 flex items-center justify-center flex-shrink-0">
        <FileText className="w-5 h-5 text-[#FF007F]" />
      </div>
      <div className="flex-1 min-w-0">
        {editing ? (
          <div className="flex items-center gap-1.5">
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') commit(); if (e.key === 'Escape') { setTitle(pdf.title); setEditing(false); } }}
              className="bg-black/40 border-white/10 text-white h-8 rounded-lg text-sm"
              autoFocus
              data-testid={`menu-pdf-title-input-${pdf.id}`}
            />
            <button onClick={commit} className="text-[#39FF14] hover:bg-white/5 rounded-full p-1.5" aria-label="Save title" data-testid={`menu-pdf-title-save-${pdf.id}`}><Check className="w-4 h-4" /></button>
            <button onClick={() => { setTitle(pdf.title); setEditing(false); }} className="text-gray-400 hover:bg-white/5 rounded-full p-1.5" aria-label="Cancel"><X className="w-4 h-4" /></button>
          </div>
        ) : (
          <>
            <p className="text-white font-semibold text-sm truncate font-['Outfit']" data-testid={`menu-pdf-title-${pdf.id}`}>{pdf.title}</p>
            <p className="text-gray-500 text-[11px] truncate">{pdf.original_filename} · {fmtSize(pdf.size || 0)}</p>
          </>
        )}
      </div>
      <div className="flex items-center gap-0.5 flex-shrink-0">
        <button onClick={() => onMove(index, -1)} disabled={index === 0} className="text-gray-400 hover:text-white disabled:opacity-20 p-1.5 rounded-full hover:bg-white/5" aria-label="Move up" data-testid={`menu-pdf-up-${pdf.id}`}><ChevronUp className="w-4 h-4" /></button>
        <button onClick={() => onMove(index, 1)} disabled={index === total - 1} className="text-gray-400 hover:text-white disabled:opacity-20 p-1.5 rounded-full hover:bg-white/5" aria-label="Move down" data-testid={`menu-pdf-down-${pdf.id}`}><ChevronDown className="w-4 h-4" /></button>
        <button onClick={() => onPreview(pdf)} className="text-gray-300 hover:text-white p-1.5 rounded-full hover:bg-white/5" aria-label="Preview" data-testid={`menu-pdf-preview-${pdf.id}`}><Eye className="w-4 h-4" /></button>
        <button onClick={() => setEditing(true)} className="text-gray-300 hover:text-white p-1.5 rounded-full hover:bg-white/5" aria-label="Rename" data-testid={`menu-pdf-rename-${pdf.id}`}><Pencil className="w-4 h-4" /></button>
        <button onClick={() => onDelete(pdf)} className="text-[#FF3B3B] hover:bg-[#FF3B3B]/10 p-1.5 rounded-full" aria-label="Delete" data-testid={`menu-pdf-delete-${pdf.id}`}><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

export const PdfMenusCard = ({ onPreview }) => {
  const [pdfs, setPdfs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [newTitle, setNewTitle] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/menu/pdfs`);
      setPdfs(r.data || []);
    } catch (e) {
      toast.error('Failed to load PDF menus', { description: e?.response?.data?.detail });
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    if (!/\.pdf$/i.test(file.name)) { toast.error('Only PDF files are supported'); return; }
    if (file.size > MAX_BYTES) { toast.error('PDF too large (max 15 MB)'); return; }
    setUploading(true); setProgress(0);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('title', newTitle.trim() || file.name.replace(/\.pdf$/i, ''));
      await axios.post(`${API}/menu/pdfs`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (ev) => { if (ev.total) setProgress(Math.round((ev.loaded / ev.total) * 100)); },
      });
      toast.success('PDF menu added');
      setNewTitle('');
      await load();
    } catch (e) {
      toast.error('Upload failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setUploading(false);
    }
  };

  const rename = async (pdf, title) => {
    try {
      await axios.put(`${API}/menu/pdfs/${pdf.id}`, { title });
      setPdfs((prev) => prev.map((p) => (p.id === pdf.id ? { ...p, title } : p)));
      toast.success('Renamed');
    } catch (e) {
      toast.error('Rename failed', { description: e?.response?.data?.detail });
    }
  };

  const remove = async (pdf) => {
    if (!window.confirm(`Remove "${pdf.title}" from the guest menu?`)) return;
    try {
      await axios.delete(`${API}/menu/pdfs/${pdf.id}`);
      setPdfs((prev) => prev.filter((p) => p.id !== pdf.id));
      toast.success('PDF removed');
    } catch (e) {
      toast.error('Delete failed', { description: e?.response?.data?.detail });
    }
  };

  const move = async (index, dir) => {
    const next = [...pdfs];
    const [item] = next.splice(index, 1);
    next.splice(index + dir, 0, item);
    setPdfs(next);
    try {
      const r = await axios.put(`${API}/menu/pdfs`, { order: next.map((p) => p.id) });
      setPdfs(r.data || next);
    } catch (e) {
      toast.error('Reorder failed', { description: e?.response?.data?.detail });
      load();
    }
  };

  return (
    <section className="mb-6 rounded-3xl border border-white/10 bg-white/[0.02] p-4 lg:p-5" data-testid="menu-pdfs-card">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="text-white font-extrabold text-base font-['Outfit'] flex items-center gap-2">
            <FileText className="w-4 h-4 text-[#FF007F]" /> PDF Menus
          </h2>
          <p className="text-gray-500 text-xs mt-0.5">Guests get a &quot;PDF Menu&quot; tab next to your itemised menu. Upload Food, Bar, Wine lists — max 15 MB each.</p>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 text-[#FF007F] animate-spin" /></div>
      ) : pdfs.length === 0 ? (
        <p className="text-gray-500 text-sm text-center py-4 border border-dashed border-white/10 rounded-2xl mb-3" data-testid="menu-pdfs-empty">No PDF menus yet.</p>
      ) : (
        <div className="space-y-2 mb-3" data-testid="menu-pdfs-list">
          {pdfs.map((p, i) => (
            <PdfRow key={p.id} pdf={p} index={i} total={pdfs.length} onRename={rename} onDelete={remove} onMove={move} onPreview={onPreview} />
          ))}
        </div>
      )}

      <div className="flex gap-2 flex-col sm:flex-row">
        <Input
          value={newTitle}
          onChange={(e) => setNewTitle(e.target.value)}
          placeholder="Title (e.g. Bar Menu) — optional"
          className="bg-black/40 border-white/10 text-white h-10 rounded-xl flex-1"
          data-testid="menu-pdf-new-title"
        />
        <Button
          type="button"
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="bg-[#FF007F] hover:bg-[#cc0066] text-white rounded-full px-5 text-xs font-bold tracking-wider h-10"
          data-testid="menu-pdf-upload-btn"
        >
          {uploading ? <><Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> {progress}%</> : <><Upload className="w-4 h-4 mr-1.5" /> UPLOAD PDF</>}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ''; }}
          data-testid="menu-pdf-file-input"
        />
      </div>
    </section>
  );
};
